import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BoardSize from 'shared/components/parameters/BoardSize';
import InstantQuote from 'shared/components/parameters/InstantQuote';
import NumberLayer from 'shared/components/parameters/NumberLayer';
import Quantities from 'shared/components/parameters/Quantities';
import ArrayXQuantity from 'shared/components/parameters/ArrayXQuantity';
import ArrayYQuantity from 'shared/components/parameters/ArrayYQuantity';
import NeedArray from 'shared/components/parameters/NeedArray';
import TabRout from 'shared/components/parameters/TabRout';
import Scoring from 'shared/components/parameters/Scoring';
import PanelSize from 'shared/components/parameters/PanelSize';
import MaterialType from 'shared/components/parameters/MaterialType';
import MaterialThickness from 'shared/components/parameters/MaterialThickness';
import FinishPlating from 'shared/components/parameters/FinishPlating';
import CopperWeight from 'shared/components/parameters/CopperWeight';
import SolderMask from 'shared/components/parameters/SolderMask';
import SilkscreenSides from 'shared/components/parameters/SilkscreenSides';
import SilkscreenColor from 'shared/components/parameters/SilkscreenColor';
import AdvancedSpecs from 'shared/components/parameters/AdavancedSpecs';
import GoldFingers from 'shared/components/parameters/GoldFingers';
import GoldThickness from 'shared/components/parameters/GoldThickness';
import MinTraceSpace from 'shared/components/parameters/MinTraceSpace';
import BlindBuriedBias from 'shared/components/parameters/BlindBuriedBias';
import Pitch from 'shared/components/parameters/Pitch';
import SmallestHoleSize from 'shared/components/parameters/SmallestHoleSize';
import PlatedSlots from 'shared/components/parameters/PlatedSlots';
import Counterbore from 'shared/components/parameters/Counterbore';
import ControlledImpedance from 'shared/components/parameters/ControlledImpedance';
import PlatedEdges from 'shared/components/parameters/PlatedEdges';
import Countersink from 'shared/components/parameters/Countersink';
import ControlledDielectric from 'shared/components/parameters/ControlledDielectric';
import SpecialInstructions from 'shared/components/parameters/SpecialInstructions';

import { selectFabricationDataForParameters } from 'offshore_pcb/pcb_fabrication/slice/selectors';
import { changeParameter, updateValidatorFlag } from 'offshore_pcb/pcb_fabrication/slice/fabricationSlice';
import { finishThickness, solderMaskColorOptions } from 'offshore_pcb/pcb_fabrication/data/selectOptions';
import { doQuote } from 'offshore_pcb/pcb_fabrication/slice/thunks';
import { validator, arrayValidator } from 'offshore_pcb/pcb_fabrication/slice/validators';
import { START } from 'shared/data/quoteSteps';

const Parameters = () => {
  const dispatch = useDispatch();
  const {
    parameters,
    loading,
    quoteStep,
    // eslint-disable-next-line no-unused-vars
    validatorFlag,
  } = useSelector(selectFabricationDataForParameters);

  const onChangeParameter = (name, value) => {
    dispatch(changeParameter({ name, value }));
  };

  const onQuoteRequest = () => {
    arrayValidator.hideMessages();
    if (validator.allValid()) {
      if (parameters.has_array === '1' && !arrayValidator.allValid()) {
        arrayValidator.showMessages();
        dispatch(updateValidatorFlag());
        return;
      }

      validator.showMessages();
      dispatch(doQuote());
    } else {
      validator.showMessages();
      dispatch(updateValidatorFlag());
    }
  };

  const props = {
    parameters,
    onChangeParameter,
    disabled: loading,
  };

  return (
    <div>
      <div className="row">
        <div className="col-md-6">
          <h3 className="product-name">{Spree.productName}</h3>
        </div>
        <div className="col-md-6 instant-quote">
          <InstantQuote
            onQuoteRequest={onQuoteRequest}
            loading={loading}
            label={
              quoteStep !== START
                ? Spree.translations.requote
                : Spree.translations.quote
            } />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <Quantities
            {...props}
            required
            disabled={loading}
            errorMessage={validator.message(
              'Quantity',
              parameters.quantities,
              'group_required|group_numeric|group_min:1,num',
            )} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <NumberLayer {...props} required />
        </div>
        <div className="col-md-6">
          <BoardSize
            {...props}
            required
            disabled={loading}
            errorMessageLength={validator.message(
              'Board Length',
              parameters.board_length,
              'required|numeric|min:0.25,num|max:20,num',
            )}
            errorMessageWidth={validator.message(
              'Board Width',
              parameters.board_width,
              'required|numeric|min:0.25,num|max:20,num',
            )} />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <NeedArray {...props} disabled={loading}>
            <div className="row py-3">
              <div className="col-md-6">
                <ArrayXQuantity
                  {...props}
                  errorMessage={arrayValidator.message(
                    'Array X Quantity',
                    parameters.array_x_qty,
                    'required|numeric|min:1,num',
                  )} />
              </div>
              <div className="col-md-6">
                <ArrayYQuantity
                  {...props}
                  disabled={loading}
                  errorMessage={arrayValidator.message(
                    'Array Y Quantity',
                    parameters.array_y_qty,
                    'required|numeric|min:1,num',
                  )} />
              </div>
              <div className="col-md-6">
                <TabRout {...props} />
              </div>
              <div className="col-md-6">
                <Scoring {...props} />
              </div>
              <div className="col-md-12">
                <PanelSize {...props} />
              </div>
            </div>
          </NeedArray>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <MaterialType {...props} />
        </div>
        <div className="col-md-6">
          <MaterialThickness {...props} options={finishThickness} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <FinishPlating {...props} />
        </div>
        <div className="col-md-6">
          <CopperWeight {...props} />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <SolderMask {...props} solderColorOptions={solderMaskColorOptions} />
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          <SilkscreenSides {...props} />
        </div>
        <div className="col-6">
          <SilkscreenColor {...props} />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <AdvancedSpecs {...props} disabled={loading}>
            <div className="row">
              <div className="col-6">
                <GoldFingers {...props} />
              </div>
              <div className="col-6">
                <GoldThickness {...props} />
              </div>
              <div className="col-6">
                <MinTraceSpace {...props} />
              </div>
              <div className="col-6">
                <BlindBuriedBias {...props} />
              </div>
              <div className="col-6">
                <Pitch {...props} />
              </div>
              <div className="col-6">
                <SmallestHoleSize {...props} />
              </div>
              <div className="col-6">
                <PlatedSlots {...props} />
              </div>
              <div className="col-6">
                <Counterbore {...props} showCollapse={false} />
              </div>
              <div className="col-6">
                <ControlledImpedance {...props} />
              </div>
              <div className="col-6">
                <PlatedEdges {...props} />
              </div>
              <div className="col-6">
                <Countersink {...props} showCollapse={false} />
              </div>
              <div className="col-6">
                <ControlledDielectric {...props} />
              </div>
            </div>
          </AdvancedSpecs>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <SpecialInstructions
            {...props}
            errorMessage={validator.message('Special Instructions', parameters.special_instructions, 'max:255')} />
        </div>
      </div>
    </div>
  );
};

export default Parameters;
